import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { SparkleIcon } from '@modernloop/shared/icons';
import { getPrimaryAccentBackground, getPrimaryAccentContrastText } from '@modernloop/shared/theme';
import {
  Avatar,
  CircularProgress,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Theme,
  Typography,
} from '@mui/material';

export type MentionSuggestionItem = {
  id: string;
  label: string;
  isAiAgent?: boolean;
  avatarUrl?: string;
};

type Props = {
  items: MentionSuggestionItem[];
  loading?: boolean;
  command: (item: { id: string; label: string }) => void;
};

export type MentionSuggestionListRef = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

// eslint-disable-next-line modernloop/validate-component-definition.cjs
const MentionSuggestionList = forwardRef<MentionSuggestionListRef, Props>(({ items, loading, command }, ref) => {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(0);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex((prev) => (prev + items.length - 1) % items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        setSelectedIndex((prev) => (prev + 1) % items.length);
        return true;
      }
      if (event.key === 'Enter') {
        if (items[selectedIndex]) {
          command(items[selectedIndex]);
        }
        return true;
      }
      return false;
    },
  }));

  if (loading) {
    return (
      <Paper elevation={8} sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Paper>
    );
  }

  if (!items.length) {
    return (
      <Paper elevation={8} sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('hiring-dashboard-common:common.noResults')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={8} sx={{ maxHeight: 240, overflow: 'auto', minWidth: 200 }}>
      <List dense disablePadding>
        {items.map((item, index) => (
          <ListItemButton key={item.id} selected={index === selectedIndex} onClick={() => command(item)} dense>
            <ListItemAvatar sx={{ minWidth: 36 }}>
              {item.isAiAgent ? (
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    background: (theme: Theme) => getPrimaryAccentBackground(theme),
                  }}
                >
                  <SparkleIcon sx={{ fontSize: 14, color: (theme: Theme) => getPrimaryAccentContrastText(theme) }} />
                </Avatar>
              ) : (
                <Avatar sx={{ width: 24, height: 24, fontSize: 12 }} src={item.avatarUrl}>
                  {item.label[0]}
                </Avatar>
              )}
            </ListItemAvatar>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ variant: 'body2', fontWeight: item.isAiAgent ? 600 : 400 }}
            />
          </ListItemButton>
        ))}
      </List>
    </Paper>
  );
});

MentionSuggestionList.displayName = 'MentionSuggestionList';

export default MentionSuggestionList;
